/**
 * TaskTreeMiddle · Overwatch Auto-System · middle column
 * ──────────────────────────────────────────────────────
 * Renders the task tree for the currently-selected status, showing
 * per-action progress markers. Clicking an action IN THE CURRENT TASK
 * jumps the runner to it. Clicking a task heading in a DIFFERENT task
 * offers to abandon the current run and switch to that task.
 *
 * Statuses that are `locked` render the tree read-only (from
 * `tasks_readonly`) so the operator can preview what work is coming.
 *
 * Props:
 *   statusRow        — the ladder entry for the active status
 *   currentTaskId    — the task_id of the currently-running workflow_run
 *                      (null when no run is active). Actions in ANY OTHER
 *                      task are greyed out + un-clickable. Task headings
 *                      in other tasks show a "switch here" affordance.
 *   activeActionId   — highlight this action row
 *   onPickAction     — (task, action) called when an action in the current
 *                      task is clicked
 *   onSwitchTask     — (task) called when a task heading (different from
 *                      currentTaskId) is clicked. The console asks for
 *                      confirm + abandons + restarts on the new task.
 */
import React from 'react';
import { Lock, Check, Play, Circle, ArrowRight, MinusCircle } from 'lucide-react';

const stateDot = (state) => {
  if (state === 'complete')    return { Icon: Check,  cls: 'text-emerald-600' };
  if (state === 'in_progress') return { Icon: Play,   cls: 'text-sky-600' };
  if (state === 'locked')      return { Icon: Lock,   cls: 'text-slate-400' };
  if (state === 'skipped_alt') return { Icon: MinusCircle, cls: 'text-slate-300' };
  return { Icon: Circle, cls: 'text-slate-400' };
};

export default function TaskTreeMiddle({
  statusRow, currentTaskId, activeActionId,
  onPickAction, onSwitchTask,
}) {
  if (!statusRow) return null;
  const isLocked = statusRow.state === 'locked';
  const isExclusive = statusRow.task_mode === 'exclusive';
  const rawTasks = isLocked ? statusRow.tasks_readonly : statusRow.tasks;

  // iter303aj-fix6 — In `exclusive` statuses, once ANY sibling task is
  // complete the alternate paths become "skipped_alt" so operators see
  // clearly that they've finished the status via one path and don't
  // wonder why the other task is "unstarted".
  const anyComplete = isExclusive && rawTasks.some(t => t.state === 'complete');
  const tasks = anyComplete
    ? rawTasks.map(t => (t.state === 'complete' ? t : { ...t, state: 'skipped_alt' }))
    : rawTasks;

  return (
    <aside className="w-72 flex-shrink-0 border-r border-slate-200 bg-slate-50/40 overflow-y-auto"
           data-testid="wf-task-tree">
      <div className="p-3 border-b border-slate-200 bg-white sticky top-0 z-10">
        <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">
          Task list · {statusRow.status_label}
        </div>
        <div className="text-sm text-slate-700 mt-0.5">
          {tasks.length} task{tasks.length === 1 ? '' : 's'}
          {isLocked && (
            <span className="ml-2 text-[10px] uppercase tracking-wider text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded">
              read-only
            </span>
          )}
        </div>
      </div>

      <div className="py-2">
        {tasks.length === 0 && (
          <div className="px-3 py-4 text-xs text-slate-400 italic">
            No tasks defined yet for this status.
          </div>
        )}
        {tasks.map(task => {
          const T = stateDot(task.state);
          const isCurrentTask = currentTaskId != null && task.id === currentTaskId;
          const isOtherTask   = currentTaskId != null && task.id !== currentTaskId;
          const isSkippedAlt  = task.state === 'skipped_alt';
          return (
            <div key={task.id} className="mb-2" data-testid={`wf-task-${task.id}`}>
              {/* Task heading — clickable if operator can switch here.
                  Skipped-alt tasks (opposite path already completed) are
                  non-clickable static rows. */}
              {(!isLocked && isOtherTask && !isSkippedAlt && onSwitchTask) ? (
                <button
                  type="button"
                  onClick={() => onSwitchTask(task)}
                  data-testid={`wf-task-heading-${task.id}`}
                  className="w-full text-left px-3 py-1.5 text-xs uppercase tracking-widest text-slate-500 hover:bg-sky-100 hover:text-sky-800 flex items-center gap-2 group"
                  title="Switch to this task (abandons current)"
                >
                  <T.Icon size={12} className={T.cls} strokeWidth={2.5} />
                  <span className="font-semibold flex-1">{task.label}</span>
                  <ArrowRight size={12} className="text-slate-300 group-hover:text-sky-600" />
                </button>
              ) : (
                <div className="px-3 py-1.5 text-xs uppercase tracking-widest text-slate-500 font-semibold flex items-center gap-2">
                  <T.Icon size={12} className={T.cls} strokeWidth={2.5} />
                  <span>{task.label}</span>
                  {isCurrentTask && (
                    <span className="ml-auto text-[9px] normal-case bg-sky-100 text-sky-700 px-1.5 py-0.5 rounded">
                      running
                    </span>
                  )}
                  {task.state === 'skipped_alt' && (
                    <span data-testid={`wf-task-skipped-alt-${task.id}`}
                      className="ml-auto text-[9px] normal-case bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded italic"
                      title="Skipped — the alternative path was completed for this status">
                      skipped · alt path
                    </span>
                  )}
                </div>
              )}

              {task.actions.map(a => {
                const A = stateDot(a.state);
                const active   = activeActionId === a.id;
                // Clickable only if this action is in the current task AND
                // the status itself isn't locked. Actions in other tasks
                // are greyed out — must switch tasks first.
                const clickable = !isLocked && !isSkippedAlt && (currentTaskId == null || isCurrentTask);
                return (
                  <button
                    key={a.id}
                    onClick={() => clickable && onPickAction && onPickAction(task, a)}
                    disabled={!clickable}
                    data-testid={`wf-action-${a.id}`}
                    style={{ paddingLeft: `${16 + (a.sub_depth || 0) * 14}px` }}
                    className={`
                      w-full text-left flex items-start gap-2 pr-3 py-1.5 text-xs
                      ${active ? 'bg-sky-100 border-l-2 border-sky-500' : 'hover:bg-slate-100 border-l-2 border-transparent'}
                      ${!clickable ? 'cursor-default opacity-40' : 'cursor-pointer'}
                      ${isOtherTask ? 'italic' : ''}
                      ${isSkippedAlt ? 'line-through opacity-40' : ''}
                    `}
                  >
                    <A.Icon size={11} className={`${A.cls} mt-0.5 flex-shrink-0`} strokeWidth={2.5} />
                    <span className={`flex-1 leading-snug ${active ? 'text-sky-900 font-semibold' : 'text-slate-700'}`}>
                      {(a.label || a.type).slice(0, 60)}
                      {a.label && a.label.length > 60 && '…'}
                    </span>
                  </button>
                );
              })}
            </div>
          );
        })}
      </div>
    </aside>
  );
}
